import { useIntl } from 'react-intl'
import { ColumnVisibility, MatchingPayoutTicket } from '../../core/_models'
import { convertUTCtoMSK, formatSum } from '../../../../../utils'
import { KTIcon } from '../../../../../_metronic/helpers'
import { AmountCell } from '../common/AmountCell'
import { PayoutTokenLink } from '../../../../common/PayoutTokenLink'
import { ClientCell } from '../../../../common/ClientCell'
import { PayoutRequisites } from '../common/PayoutRequisites'
import clsx from 'clsx'
import { MouseEvent, useCallback, useMemo, useRef, useState } from 'react'
import { EMatchingStatus } from '../../../../../types/enums'
import { formatMatchingTimeout } from '../../core/matchingTimeout'
import { MatchCountCell } from './MatchCountCell'
import { HaltHoldsHint } from './HaltHoldsHint'
import { PayoutRowActions } from '../common/PayoutRowActions'
import { PayoutDepositsTable } from '../common/PayoutDepositsTable'
import { VirtualPaymentState } from '../common/usePayoutTicketActions'

interface Props {
  ticket: MatchingPayoutTicket
  nowMs: number
  onPartialComplete?: (ticketToken: string, paused?: boolean) => void
  onCancel?: (ticketToken: string, paused?: boolean) => void
  visibility: ColumnVisibility
  visibleColumns: string[]
  canManageTickets: boolean
  canCreateVirtualPayment: boolean
  /** Ручная переотправка КБ — отдельное право, список ролей в matchingCapabilities */
  canResend: boolean
  virtualPaymentState?: VirtualPaymentState
  onOverpaymentCreated?: (compensationPayoutToken: string) => void
  onRequisiteReplaced?: () => void
  /** Переоплата ушла на кассу — строка изменилась, список нужно перечитать (CPAYMENT-1835) */
  onOverpaymentHandedToCassa?: () => void
}

const ActivePayoutTicketItem = ({
  ticket,
  nowMs,
  onPartialComplete,
  onCancel,
  visibility,
  visibleColumns,
  canManageTickets,
  canCreateVirtualPayment,
  canResend,
  virtualPaymentState,
  onOverpaymentCreated,
  onRequisiteReplaced,
  onOverpaymentHandedToCassa,
}: Props) => {
  const intl = useIntl()
  const [isExpanded, setIsExpanded] = useState<boolean>(false)

  // Депозиты, по которым переоплата уже заведена в этом сеансе, — кнопка по ним гаснет сразу.
  // Серверный запрет держится, только пока компенсационная выплата не финальна, а она может стать
  // финальной в ту же секунду: перечитанный список тогда снова разрешит переоплату, и второй клик
  // заведёт ещё одну. Ref, а не состояние: перерисовку даёт закрытие модалки в том же обработчике.
  // Живёт в строке, а не в таблице депозитов: таблица размонтируется при сворачивании.
  const overpaymentLock = useRef<Set<number>>(new Set())

  const isExpandable = useMemo(() => {
    return ticket?.paymentTicketList?.length > 0 && visibility.paymentTicketList
  }, [ticket, visibility])

  const toggleOpen = useCallback(
    (e: MouseEvent<HTMLTableRowElement>) => {
      if (!isExpandable) return
      // Исключаем клики по ссылкам
      if ((e.target as HTMLElement)?.closest('a')) return
      setIsExpanded((prev) => !prev)
    },
    [isExpandable]
  )

  return (
    <>
      <tr>
        <td className='text-center align-middle'>
          <span className={clsx(isExpandable && 'cursor-pointer')} onClick={toggleOpen}>
            <KTIcon
              iconName={isExpanded ? 'minus-square' : 'plus-square'}
              className={`fs-3 text-muted ${isExpandable ? 'cursor-pointer' : 'opacity-50'}`}
            />
          </span>
        </td>
        {visibility.ticketToken && (
          <td>
            <p className='mb-0'>
              <PayoutTokenLink token={ticket.ticketToken} />
            </p>
            {/* CPAYMENT-1721: ID тикета мерчанта живёт под токеном, отдельной колонки под него нет. */}
            {visibility.merchantId && (
              <div className='d-flex-column mt-2'>
                <p className='fw-bold fs-9 mb-0'>ID тикета мерчанта:</p>
                <span className='fs-9'>{ticket.merchantTransactionId || '-'}</span>
              </div>
            )}
          </td>
        )}
        {visibility.user && <ClientCell externalId={ticket.externalUserId} token={ticket.clientToken} />}
        {visibility.amount && (
          <AmountCell
            amount={ticket?.amount}
            expectedAmount={ticket?.expectedAmount}
            isAmountChanged={false}
            showExpectedAmount={false}
          />
        )}
        {visibility.createdAt && <td>{convertUTCtoMSK(ticket.createdAt)}</td>}
        {visibility.matchCount && (
          <td className='align-middle'>
            <MatchCountCell ticket={ticket} />
          </td>
        )}
        {visibility.matchingRemainder && <td>{formatSum(ticket.matchingRemainder)}</td>}
        {visibility.depositRange && (
          <td>
            {(() => {
              type Range = [number | null | undefined, number | null | undefined]
              const ranges = (ticket.depositRange ?? []) as Range[]

              if (!ranges.length) return null

              const fmtL = (v: number | null | undefined) =>
                v === null || v === undefined ? '?' : formatSum(Math.ceil(v))
              const fmtU = (v: number | null | undefined) =>
                v === null || v === undefined ? '?' : formatSum(Math.floor(v))

              return (
                <>
                  {ranges.map(([min, max], i) => (
                    <span key={i}>
                      <span className="badge">
                        {fmtL(min)} - {fmtU(max)}
                      </span>
                      {i < ranges.length - 1 ? ', ' : null}
                    </span>
                  ))}
                </>
              )
            })()}
          </td>
        )}
        {visibility.merchant && <td>{ticket.merchant || '-'}</td>}
        {visibility.status && (
          <td>{intl.formatMessage({ id: 'ticket.status.' + ticket.status })}</td>
        )}
        {visibility.matchingStatus && (
          <td>
            {ticket.matchingStatus ? (
              <div className='d-flex flex-column align-items-start gap-1'>
                <HaltHoldsHint
                  ticket={ticket}
                  label={intl.formatMessage({ id: 'matching.status.' + ticket.matchingStatus })}
                />
                {ticket.matchingStatus === EMatchingStatus.PROCESSING && ticket.matchingPaused && (
                  <span
                    className='badge badge-light-warning'
                    title='Новые пополнения на выплату не назначаются'
                  >
                    Приостановлена
                  </span>
                )}
                {ticket.matchingStatus === EMatchingStatus.PROCESSING &&
                  (() => {
                    const view = formatMatchingTimeout(ticket.matchingTimeoutAt, nowMs)
                    if (!view) return null
                    return (
                      <span className={clsx('badge', view.danger ? 'badge-danger' : 'badge-light')}>
                        {view.text}
                      </span>
                    )
                  })()}
              </div>
            ) : (
              '-'
            )}
          </td>
        )}

        {visibility.requisites && (
          <td>
            <div className='d-flex flex-column gap-1'>
              {ticket.paymentMethod && (
                <div>
                  <span className='text-muted fs-6'>Тип метода выплаты:</span>
                  <br />
                  <span className='fw-bold'>{ticket.paymentMethod}</span>
                </div>
              )}
              {/* CPAYMENT-1713: у выплаты может быть несколько каналов — остальные раскрываются
                  по наведению, см. PayoutRequisites. */}
              <PayoutRequisites
                requisites={ticket.requisites}
                fallback={{
                  identifier: ticket.matchingTicketRequisite,
                  bank: ticket.matchingTicketBank,
                  cardholder: ticket.matchingTicketCardholder,
                }}
              />
            </div>
          </td>
        )}

        {(canManageTickets || canResend) && (
          <td className='text-end'>
            <PayoutRowActions
              ticket={ticket}
              canManageTickets={canManageTickets}
              canCreateVirtualPayment={canCreateVirtualPayment}
              canResend={canResend}
              virtualPaymentState={virtualPaymentState}
              onPartialComplete={onPartialComplete}
              onCancel={onCancel}
              onRequisiteReplaced={onRequisiteReplaced}
            />
          </td>
        )}
      </tr>
      {visibility.paymentTicketList && isExpanded && ticket.paymentTicketList?.length ? (
        <tr>
          <td colSpan={visibleColumns.length} className='p-0'>
            <div className='m-4 border bg-background-light'>
              <PayoutDepositsTable
                ticket={ticket}
                visibility={visibility}
                onOverpaymentCreated={onOverpaymentCreated}
                onOverpaymentHandedToCassa={onOverpaymentHandedToCassa}
                overpaymentLock={overpaymentLock}
              />
            </div>
          </td>
        </tr>
      ) : null}
    </>
  )
}

export default ActivePayoutTicketItem
